/**
 * mdo-recording.js — Trimmed session audio recorder for VibeLive transcription.
 *
 * Bundled with the main SDK and imported statically by `channel_comms_class.js`.
 * It is instantiated lazily on first `startRecordingAudio()`, so apps that never
 * transcribe pay only a few KB of parse cost and no runtime cost. Exports a single
 * default: `MdoRecorder`.
 *
 * Responsibilities (deliberately minimal):
 *   - Mix one or more live audio tracks into a single master stream (Web Audio).
 *   - Record that master mix as a sequence of self-contained WebM/Opus segments (~30s),
 *     uploading each one mid-session so the server can transcribe progressively. The final
 *     segment (emitted on stop) is flagged `is_final` so the server knows the session is done.
 *   - Hand each finished segment to an injected `upload` callback (networking stays in fetch.js).
 *
 * The recorder only RECORDS + uploads; the actual transcription happens server-side
 * (AssemblyAI via AaiTranscriber). Cost optimisations from the legacy AudioProcessor
 * (silence removal, speed-up, multi-format, local download, per-track recording) are
 * intentionally dropped — they belong server-side.
 */
class MdoRecorder {
    /**
     * @param {string} channelId
     * @param {Object} opts
     * @param {(blob: Blob, meta: Object) => Promise<any>} opts.upload  Persists a finished segment.
     * @param {(state: string) => void} [opts.onState]  'recording' | 'uploading' | 'processing' | 'idle'.
     * @param {number} [opts.segmentSeconds]  Length of each uploaded segment (default 30).
     */
    constructor(channelId, { upload, onState, segmentSeconds } = {}) {
        this.channelId = channelId;
        this.upload = upload;
        this.onState = onState || (() => {});
        this.segmentMs = Math.max(5, segmentSeconds || 30) * 1000; // roll a fresh segment file this often
        this.audioContext = null;
        this.masterDestination = null;
        this.trackNodes = new Map();      // trackId -> { source, gain }
        this.isRecording = false;
        this._mimeType = 'audio/webm';
        this._currentRec = null;          // MediaRecorder for the in-progress segment
        this._segmentTimer = null;        // interval timer that rolls segments
    }

    /**
     * Begin recording, seeded with any currently-available audio tracks.
     * @param {MediaStreamTrack[]} [initialTracks]
     */
    async start(initialTracks = []) {
        if (this.isRecording) return this;
        if (typeof MediaRecorder === 'undefined') {
            console.warn('MdoRecorder: MediaRecorder unavailable — recording skipped.');
            return this;
        }
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (this.audioContext.state === 'suspended') {
            try { await this.audioContext.resume(); } catch (_) {}
        }
        this.masterDestination = this.audioContext.createMediaStreamDestination();
        this.isRecording = true;

        for (const track of (initialTracks || [])) this.addTrack(track);

        let mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
        this._mimeType = mimeType || 'audio/webm';

        // Record in self-contained segments so each can be uploaded + transcribed mid-session.
        // The audio graph (AudioContext / master mix / tracks) persists across segments; only the
        // per-segment MediaRecorder is cycled. Tracks added later flow into subsequent segments.
        this._beginSegment();
        this._segmentTimer = setInterval(() => this._rollSegment(), this.segmentMs);

        this.onState('recording');
        return this;
    }

    /** Open a fresh segment recorder on the persistent master mix. */
    _beginSegment() {
        const chunks = [];
        const segStart = new Date().toISOString();
        const rec = new MediaRecorder(
            this.masterDestination.stream,
            this._mimeType ? { mimeType: this._mimeType } : undefined
        );
        rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
        rec.onstop = () => { this._uploadSegment(chunks, segStart, new Date().toISOString(), rec.__isFinal === true); };
        rec.start(1000); // gather data each second
        this._currentRec = rec;
    }

    /** Close the current segment (uploads it) and immediately open the next — overlap avoids gaps. */
    _rollSegment() {
        if (!this.isRecording) return;
        const oldRec = this._currentRec;
        this._beginSegment(); // start the next segment first so the mix is never unrecorded
        try {
            if (oldRec && oldRec.state !== 'inactive') { oldRec.__isFinal = false; oldRec.stop(); }
        } catch (e) { console.error('MdoRecorder.rollSegment failed:', e); }
    }

    /**
     * Add a live audio track to the master mix during an active recording.
     * Safe to call before start() returns or with duplicate/null tracks.
     * @param {MediaStreamTrack} track
     */
    addTrack(track) {
        if (!track || !this.isRecording || !this.audioContext) return this;
        const id = track.id || `t_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        if (this.trackNodes.has(id)) return this;
        try {
            const stream = new MediaStream([track]);
            const source = this.audioContext.createMediaStreamSource(stream);
            const gain = this.audioContext.createGain();
            gain.gain.value = 1.0;
            source.connect(gain);
            gain.connect(this.masterDestination);
            this.trackNodes.set(id, { source, gain });
        } catch (e) {
            console.error('MdoRecorder.addTrack failed:', e);
        }
        return this;
    }

    /** Stop recording; the final segment uploads (is_final=true) via its onstop, then the graph is torn down. */
    async stop() {
        if (!this.isRecording) return;
        this.isRecording = false;
        if (this._segmentTimer) { clearInterval(this._segmentTimer); this._segmentTimer = null; }
        const rec = this._currentRec;
        this._currentRec = null;
        try {
            if (rec && rec.state !== 'inactive') {
                rec.__isFinal = true;
                rec.stop(); // → onstop → _uploadSegment(..., isFinal=true) → teardown
            } else {
                this._teardownGraph();
                this.onState('idle');
            }
        } catch (e) {
            console.error('MdoRecorder.stop failed:', e);
        }
    }

    /** Upload one finished segment. On the final segment, tear down the audio graph afterwards. */
    async _uploadSegment(chunks, utcStart, utcEnd, isFinal) {
        if (chunks && chunks.length) {
            const blob = new Blob(chunks, { type: this._mimeType });
            const meta = {
                channel_id: this.channelId,
                utc_start_ts: utcStart,
                utc_end_ts: utcEnd,
                is_final: isFinal,
            };
            try {
                if (isFinal) this.onState('uploading');
                if (this.upload) await this.upload(blob, meta);
            } catch (e) {
                console.error('MdoRecorder upload failed:', e);
            }
        }
        if (isFinal) {
            this._teardownGraph();
            this.onState('processing');
            this.onState('idle');
        }
    }

    /** Disconnect and release the persistent audio graph (called once, after the final segment). */
    _teardownGraph() {
        for (const { source, gain } of this.trackNodes.values()) {
            try { source.disconnect(); gain.disconnect(); } catch (_) {}
        }
        this.trackNodes.clear();
        try { if (this.audioContext) this.audioContext.close(); } catch (_) {}
        this.audioContext = null;
        this.masterDestination = null;
        this._currentRec = null;
    }
}

export default MdoRecorder;
